/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from 'react';
import { IConfigUrl, ISeller, IUser } from '../../models';

type TUseMaasAccountProps = { config?: IConfigUrl };

type TMaasAccount = {
    user: IUser;
    sellers: ISeller[];
    sellerPermissions: any;
    currentServices: any;
    currentSeller: ISeller | undefined;
    setCurrentSeller: (tenantId: string) => void;
    onSellerChanged: { status: string; sellers: ISeller[] | ISeller | null };
    loading: boolean;
    error: { error: boolean; message: string };
    setError: React.Dispatch<
        React.SetStateAction<{
            error: boolean;
            message: string;
        }>
    >;
    responseError: any;
};

const useMaasAccount = ({ config }: TUseMaasAccountProps): TMaasAccount => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<{ error: boolean; message: string }>({ error: false, message: '' });
    const [sellers, setSellers] = useState<ISeller[]>([]);
    const [user, setUser] = useState<IUser>({ email: '', tenantIdMagalu: '' });
    const [currentSellerInternal, setCurrentSellerInternal] = useState<ISeller>();
    const [sellerPermissions, setSellerPermissions] = useState([]);
    const [responseError, setResponseError]: any = useState(null);
    const [onSellerChanged, setOnSellerChanged] = useState<{ status: string; sellers: ISeller[] | ISeller | null }>({
        status: 'loading',
        sellers: null,
    });

    const token = config?.keycloakToken;

    // use config urls or environment variable
    const MAAS_ACCOUNT_API_URL = config?.url || process.env.REACT_APP_MAAS_ACCOUNT_API_URL;
    if (!MAAS_ACCOUNT_API_URL) throw Error('variável de ambiente REACT_APP_MAAS_ACCOUNT_API_URL é obrigatório');

    const getSellers = useCallback(() => {
        const headers = new Headers({
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            credentials: 'include',
        });

        const request = new Request(`${MAAS_ACCOUNT_API_URL}/auth`, { headers });
        return fetch(request);
    }, [MAAS_ACCOUNT_API_URL, token]);

    const getSellersPermissions = useCallback(() => {
        const headers = new Headers({
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            credentials: 'include',
        });

        const request = new Request(`${MAAS_ACCOUNT_API_URL}/auth/permissions`, { headers });

        return fetch(request).then((response) => {
            if (!response.ok) setResponseError({ status: response.status, statusText: response.statusText });
            return response;
        });
    }, [MAAS_ACCOUNT_API_URL, token]);

    const setCurrentSeller = (tenantId: string) => {
        setOnSellerChanged({ status: 'loading', sellers: null });

        const seller = sellers.find((seller) => seller.tenantId === tenantId);

        if (seller) {
            setCurrentSellerInternal(seller);
            sessionStorage.setItem('maas-tenantId', tenantId);
            setOnSellerChanged({ status: 'completed', sellers: seller });
        } else {
            const sellerInternal = sellers?.[0] || null;
            setCurrentSellerInternal(sellerInternal);
            sessionStorage.setItem('maas-tenantId', sellerInternal?.tenantId || '');
            setOnSellerChanged({ status: 'completed', sellers: sellerInternal });
            setError({
                error: true,
                message: 'Não foi possivel alterar para organização selecionada.',
            });
            console.error(
                `O tenantId ${tenantId} não pertece a esse usuário. tenantId ${sellerInternal?.tenantId} foi selecionado por padrão.`,
            );
        }
    };

    useEffect(() => {
        setOnSellerChanged({ status: 'loading', sellers: null });

        // executa os request para sellers e permission
        Promise.all([getSellers(), getSellersPermissions()])
            .then((responses) => Promise.all(responses.map((r) => r.json())))
            .then(([result, permissions]) => {
                if (!result.sellers || !Object.keys(permissions || {}).length) {
                    setError({ error: true, message: 'Não existe permissões cadastradas' });
                }

                setUser({ email: result.email, tenantIdMagalu: result.tenantIdMagalu });
                setSellers(result.sellers);
                setSellerPermissions(permissions);
                setOnSellerChanged({
                    status: 'completed',
                    sellers: result.sellers,
                });
            })
            .finally(() => setLoading(false));
    }, [getSellersPermissions, getSellers]);

    return {
        user,
        sellers,
        sellerPermissions,
        currentServices: currentSellerInternal?.services,
        setCurrentSeller,
        currentSeller: currentSellerInternal,
        onSellerChanged,
        loading,
        error,
        setError,
        responseError,
    };
};

export default useMaasAccount;
